import {
  BaseMetric,
  VerticalOption,
  validateDateRange,
} from '@amzn/aet-galactus-kit';
import Text from '@amzn/meridian/text';
import { isInteger } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { getCscEntityPrefix } from '../../../../config/Config';
import DataState from '../../../../Types/DataState';
import { ExtendedUser } from '../../../../Types/ExtendedUser';
import { TeamProfile } from '../../../../Types/TeamProfile';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import { EPOCH_START } from '../../../../Util/GlobalConstants';
import { badgeTypeAssign } from '../QCS/Util/generalUtil';
import {
  ACHTSubMetrics,
  CCXSubMetrics,
  ConcessionRateSubMetrics,
  ConsultRateSubMetrics,
  DARTSubMetrics,
  DCRSubMetrics,
  DSRSubMetrics,
  SDRSubMetrics,
  HMDResponseRateSubMetrics,
  LighthouseFilters,
  GeneralMetricType,
  LighthouseMetricOption,
  LighthouseMetrics,
  PolicyAdherenceSubmetrics,
  PRRConcessionIssuedDataSubmetrics,
  PRRNoConcessionDataSubmetrics,
  PRRSubMetrics,
  RAPSubmetrics,
  TransferRateSubMetrics,
  TrendDatapoint,
  TypeMessageMap,
  UnderstanabilitySubMetrics,
  HMDOfferRateSubMetrics,
  CCXNegSubMetrics,
  CCXNegOneboxSubMetrics,
} from './types';
import moment from 'moment';

export const SUBMETRIC_FONT_SIZE = 'b200';
export const PASRED_FAIL_NULL = '-';
export const SUBMETRIC_ROW_WIDTHS = ['45%', '25.9%'];
export const DISPLAY = 'DISPLAY';
export const NOTDISPLAY = 'NOTDISPLAY';
const QCSVertical = ['Total_QCS', 'Consumer_QCS'];

export const parseFixedString = (
  value: number,
  isPercentage: boolean,
  isTrend: boolean
): string => {
  // Parsed Value After Percentage Check - parsedValueAPC
  const parsedValueAPC = isPercentage ? value * 100 : value;

  // Parsed Value After Integer Check - parsedValueAIC
  const parsedValueAIC = isInteger(parsedValueAPC)
    ? parseInt(`${parsedValueAPC}`).toString()
    : parsedValueAPC.toFixed(1).toString();

  const parsedFixedString =
    isPercentage && isTrend
      ? parsedValueAIC
      : isPercentage
        ? `${parsedValueAIC}%`
        : parsedValueAIC;

  return parsedFixedString;
};

export const extractGeneralMetricValue = (
  metrics: GeneralMetricType,
  networkAverage?: boolean
) => {
  if (metrics === undefined) {
    return null;
  }
  const baseMetric = metrics.topLevelMetrics.baseMetric;
  return networkAverage ? baseMetric.networkAverage : baseMetric.datapoint;
};

export const extractMetricValue = (
  metricOption: string,
  metrics?: LighthouseMetrics,
  networkAverage?: boolean
) => {
  return extractGeneralMetricValue(
    metrics![metricOption as keyof LighthouseMetrics] as GeneralMetricType,
    networkAverage
  );
};

const computeMetricNumberValue = (
  metricOption: string,
  metrics?: LighthouseMetrics,
  networkAverage?: boolean
): number => {
  const data = extractMetricValue(metricOption, metrics, networkAverage);
  if (data !== null) {
    if (
      (metricOption as keyof LighthouseMetrics) === LighthouseMetricOption.ACHT
    ) {
      return parseFloat(data.replace(':', '.'));
    }
    return parseFloat(data);
  }
  return Number.MAX_VALUE;
};

export const metricComprarator = (
  a: LighthouseMetrics,
  b: LighthouseMetrics,
  metricOption: string,
  direction: string
): number => {
  const m1 = computeMetricNumberValue(metricOption, a);
  const m2 = computeMetricNumberValue(metricOption, b);
  return direction === 'ascending' ? m1 - m2 : m2 - m1;
};

export const metricCompraratorBenchmark = (
  a: LighthouseMetrics,
  b: LighthouseMetrics,
  metricOption: string,
  direction: string
): number => {
  const m1 = computeMetricNumberValue(metricOption, a);
  const m2 = computeMetricNumberValue(metricOption, b);
  const benchmark1 = computeMetricNumberValue(metricOption, a, true);
  const benchmark2 = computeMetricNumberValue(metricOption, b, true);
  const diff1 = m1 - benchmark1;
  const diff2 = m2 - benchmark2;
  return direction === 'ascending' ? diff1 - diff2 : diff2 - diff1;
};

export const parseStringInOrder = (
  metricOption: string,
  metrics?: LighthouseMetrics,
  networkAverage?: boolean
) => {
  const metricValue = extractMetricValue(metricOption, metrics, networkAverage);

  if (metricValue !== null) {
    if (metricOption === 'QCS') {
      const badge = badgeTypeAssign(metricValue).toString();
      return `${parseFixedString(
        parseFloat(metricValue),
        PERCENTAGE_TABLE[metricOption as keyof MetricBooleanMap],
        false
      )} (${badge})`;
    } else
      return parseFixedString(
        parseFloat(metricValue),
        PERCENTAGE_TABLE[metricOption as keyof MetricBooleanMap],
        false
      );
  }
};

export const topLevelAllOpen = (input: any, setter: Function): boolean => {
  const distinct: boolean[] = [...new Set<boolean>(input)];
  if (distinct.length === 2) return false;

  if (distinct[0]) {
    setter(true);
  }

  return distinct[0];
};

export const topLevelAllClose = (
  input: any,
  setter: Function,
  stateSetter: Function,
  buttonHitSetter: Function,
  expandAll: boolean
): boolean => {
  const distinct: boolean[] = [...new Set<boolean>(input)];
  if (distinct.length === 2) return false;

  if (!distinct[0]) {
    if (expandAll) {
      setter(false);
      stateSetter(DataState.LOADING);
      buttonHitSetter(false);
    }
    return true;
  }
  return false;
};

export interface MetricBooleanMap {
  RAP: boolean;
  CCX: boolean;
  CCX_NEG: boolean;
  CCX_NEG_ONEBOX: boolean;
  TR: boolean;
  DART: boolean;
  DCR: boolean;
  ACHT: boolean;
  DSR: boolean;
  SDR: boolean;
  CONSULT_RATE: boolean;
  POLICY_ADHERENCE: boolean;
  PRRNC: boolean;
  PRRCI: boolean;
  CONCESSION_RATE: boolean;
  UNDERSTANDABILITY: boolean;
  UNDERSTANDABILITY_NO_CONCESSION: boolean;
  UNDERSTANDABILITY_CONCESSION_ISSUED: boolean;
  PRR: boolean;
  HMD_OFFER_RATE: boolean;
  HMD_RESPONSE_RATE: boolean;
  QCS: boolean;
  FCR: boolean;
  ACW: boolean;
  HOLD_TIME: boolean;
  HIGH_SILENCE_RATE: boolean;
}

export const PERCENTAGE_TABLE: MetricBooleanMap = {
  RAP: true,
  CCX: true,
  CCX_NEG: true,
  CCX_NEG_ONEBOX: true,
  TR: true,
  DART: true,
  DCR: true,
  ACHT: false,
  CONSULT_RATE: true,
  DSR: true,
  SDR: true,
  POLICY_ADHERENCE: true,
  PRRNC: true,
  PRRCI: true,
  UNDERSTANDABILITY: true,
  UNDERSTANDABILITY_NO_CONCESSION: true,
  UNDERSTANDABILITY_CONCESSION_ISSUED: true,
  CONCESSION_RATE: true,
  PRR: true,
  HMD_OFFER_RATE: true,
  HMD_RESPONSE_RATE: true,
  QCS: false,
  FCR: true,
  ACW: false,
  HOLD_TIME: false,
  HIGH_SILENCE_RATE: true,
};

export const TIME_TABLE: MetricBooleanMap = {
  RAP: false,
  CCX: false,
  CCX_NEG: false,
  CCX_NEG_ONEBOX: false,
  TR: false,
  DART: false,
  DCR: false,
  ACHT: true,
  DSR: false,
  SDR: false,
  CONSULT_RATE: false,
  POLICY_ADHERENCE: false,
  PRRCI: false,
  PRRNC: false,
  UNDERSTANDABILITY: false,
  UNDERSTANDABILITY_NO_CONCESSION: false,
  UNDERSTANDABILITY_CONCESSION_ISSUED: false,
  CONCESSION_RATE: false,
  PRR: false,
  HMD_OFFER_RATE: false,
  HMD_RESPONSE_RATE: false,
  QCS: false,
  FCR: false,
  ACW: true,
  HOLD_TIME: true,
  HIGH_SILENCE_RATE: false,
};

export enum TeamTableSortMethod {
  METRIC_VALUE = 'METRIC_VALUE',
  DIFF_TO_BENCHMARK = 'DIFF_TO_BENCHMARK',
}

export const COMMON_USAGE_MAP = {
  networkAverage: {
    titleMessage: {
      id: 'cs_perfportal_network_benchmark',
      defaultMessage: 'Network Benchmark',
    },
    popoverMessage: {
      id: 'cs_perfportal_network_benchmark',
      defaultMessage: 'Network Benchmark',
    },
    definition: {
      id: 'cs_perfportal_network_benchmark_definition',
      defaultMessage: 'is the average score of teams with a similar skill mix',
    },
  },
  scoreMessage: {
    headerChange: {
      id: 'cs_perfportal_score_text',
      defaultMessage: 'Your score is',
    },
    headerSame: {
      id: 'cs_perfportal_score_text_no_change',
      defaultMessage: 'Your score has remained the same',
    },
    footer: {
      id: 'cs_perfportal_time_since',
      defaultMessage: 'since',
    },
  },
  showMore: {
    id: 'cs_perfportal_showMore',
    defaultMessage: 'Show more',
  },
  qcsContextOne: {
    id: 'cs_perfportal_qcs_context_one',
    defaultMessage: 'The higher the score, the better the quality;',
  },
  qcsContextTwo: {
    id: 'cs_perfportal_qcs_context_two',
    defaultMessage: 'with 75 being an average performance.',
  },
  historicalTeamLevelMetricsInformation: {
    id: 'cs_perfportal_historical_team_level_metrics_information',
    defaultMessage:
      'Team-level metrics include data from all associates who were on the team during the selected timeframe. Associate-level metrics only reflect associates who are on the team currently.',
  },
};

export const CARD_TYPE_MESSAGE_MAP: TypeMessageMap = {
  RAP: {
    titleMessage: {
      id: 'cs_perfportal_rap',
      defaultMessage: 'RAP',
    },
    subTitleMessage: {
      id: 'cs_perfportal_rap_subtitle',
      defaultMessage: 'Repeat Adjusted Positive Response Rate',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_rap_definition',
        defaultMessage:
          'is the percentage of "Yes" responses, after adjustments for repeat contacts in the following 48 hours, out of the total HMD responses.',
      },
      formula: {
        id: 'cs_perfportal_rap_formula',
        defaultMessage:
          'HMD Yes Adjusted / (HMD Yes Adjusted + HMD No Adjusted)',
      },
    },
    // ToDo - Utlize this logic in other metrics as well - to uniform our logic by using this text map - so we only need to change in one place
    submetric: {
      totalHMDResponses: {
        id: 'cs_perfportal_totalHMDResponses',
        defaultMessage: 'Total HMD Responses',
      },
      hmdYesAdjusted: {
        id: 'cs_perfportal_hmdYesAdjusted',
        defaultMessage: 'HMD Yes Adjusted',
      },
      hmdYesResponses: {
        id: 'cs_perfportal_hmdYesResponses',
        defaultMessage: 'HMD Yes Responses',
      },
    },
  },
  CCX: {
    titleMessage: {
      id: 'cs_perfportal_ccx',
      defaultMessage: 'CCX',
    },
    subTitleMessage: {
      id: 'cs_perfportal_ccx_subtitle',
      defaultMessage: 'Customer Contact Experience Overall',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_ccx_definition',
        defaultMessage:
          'is the percentage of 4 to 5-star responses to the "Overall Service" Customer Contact Experience (CCX) rating. This rating is also known as "Helpfulness". ',
      },
      formula: {
        id: 'cs_perfportal_ccx_formula',
        defaultMessage:
          'Positive Overall Service Responses / Total Overall Service Responses',
      },
    },
  },
  CCX_V2: {
    titleMessage: {
      id: 'cs_perfportal_ccx_topbox',
      defaultMessage: 'CCX Overall Service (Top-Two Box)',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_ccx_topbox_definition',
        defaultMessage:
          'is the percentage of 4 to 5-star responses to the "Overall Service" Customer Contact Experience (CCX) rating.',
      },
      formula: {
        id: 'cs_perfportal_ccx_topbox_formula',
        defaultMessage:
          'Positive Overall Service Responses / Total Overall Service Responses',
      },
    },
  },
  CCX_NEG: {
    titleMessage: {
      id: 'cs_perfportal_ccx_bottombox',
      defaultMessage: 'CCX Overall Service (Bottom-Two Box)',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_ccx_bottombox_definition',
        defaultMessage:
          'is the percentage of 1 to 2-star responses to the "Overall Service" Customer Contact Experience (CCX) rating.',
      },
      formula: {
        id: 'cs_perfportal_ccx_bottombox_formula',
        defaultMessage:
          'Negative Overall Service Responses / Total Overall Service Responses',
      },
    },
  },
  CCX_NEG_ONEBOX: {
    titleMessage: {
      id: 'cs_perfportal_ccx_bottom_onebox',
      defaultMessage: 'CCX Overall Service (Bottom Box)',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_ccx_bottom_onebox_definition',
        defaultMessage:
          'is the percentage of 1-star responses to the "Overall Service" Customer Contact Experience (CCX) rating.',
      },
      formula: {
        id: 'cs_perfportal_ccx_bottom_onebox_formula',
        defaultMessage:
          'Negative Overall Service Responses / Total Overall Service Responses',
      },
    },
  },
  TR: {
    titleMessage: {
      id: 'cs_perfportal_tr',
      defaultMessage: 'Transfer Rate',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_transfer_rate_definition',
        defaultMessage:
          'is the percentage of handled contacts which resulted in a transfer.',
      },
      formula: {
        id: 'cs_perfportal_transfer_rate_formula',
        defaultMessage: 'Transferred Contacts / Handled Contacts',
      },
    },
  },
  DART: {
    titleMessage: {
      id: 'cs_perfportal_dart',
      defaultMessage: 'DART',
    },
    subTitleMessage: {
      id: 'cs_perfportal_dart_subtitle',
      defaultMessage: 'Dynamic Andon Response Tool',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_dart_definition',
        defaultMessage:
          'is the percentage of handled contacts where the associate used DART to get assistance.',
      },
      formula: {
        id: 'cs_perfportal_dart_formula',
        defaultMessage: 'DART Used Contacts / Handled Contacts',
      },
    },
  },
  DCR: {
    titleMessage: {
      id: 'cs_perfportal_dcr',
      defaultMessage: 'DCR',
    },
    subTitleMessage: {
      id: 'cs_perfportal_dcr_subtitle',
      defaultMessage: 'Discretionary Concession Rate',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_dcr_definition',
        defaultMessage: `is the percentage of contacts for which the associate added a Promo GC (excludes Out Of Warranty) or Gift Card to the customer's account, out of total resolved contacts.`,
      },
      formula: {
        id: 'cs_perfportal_dcr_formula',
        defaultMessage: 'Gift Card Issued Contacts / Resolved Contacts',
      },
    },
  },
  ACHT: {
    titleMessage: {
      id: 'cs_perfportal_acht',
      defaultMessage: 'ACHT',
    },
    subTitleMessage: {
      id: 'cs_perfportal_acht_subtitle',
      defaultMessage: 'Average Concurrent Handle Time',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_acht_definition',
        defaultMessage:
          'is the handle time spent per handled contact, after time adjustments for overlapping non-phone contacts.',
      },
      formula: {
        id: 'cs_perfportal_acht_formula',
        defaultMessage: 'Total Concurrent Handle Time / Handled Contacts',
      },
    },
  },
  DSR: {
    titleMessage: {
      id: 'cs_perfportal_dsr',
      defaultMessage: 'DSR',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_dsr_definition',
        defaultMessage:
          'is the percentage of the total packages handled which resulted in delivery success.',
      },
      formula: {
        id: 'cs_perfportal_dsr_formula',
        defaultMessage: 'DSR Success / DSR Denominator',
      },
    },
  },
  SDR: {
    titleMessage: {
      id: 'cs_perfportal_sdr',
      defaultMessage: 'SDR',
    },
    subTitleMessage: {
      id: 'cs_perfportal_sdr_subtitle',
      defaultMessage: 'Same Day Repeat',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_sdr_definition',
        defaultMessage:
          'is the percentage of the total contacts handled which resulted in same day delivery repeat.',
      },
      formula: {
        id: 'cs_perfportal_sdr_formula',
        defaultMessage: 'SDR Success / SDR Denominator',
      },
    },
  },
  CONSULT_RATE: {
    titleMessage: {
      id: 'cs_perfportal_cr',
      defaultMessage: 'Consult Rate',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_cr_definition',
        defaultMessage:
          'is the percentage of consult contacts out of total handled contacts.',
      },
      formula: {
        id: 'cs_perfportal_cr_formula',
        defaultMessage: 'Consult Contacts / Handled Contacts',
      },
    },
  },
  POLICY_ADHERENCE: {
    titleMessage: {
      id: 'cs_perfportal_pa',
      defaultMessage: 'Policy Adherence',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_pa_definition',
        defaultMessage:
          'is the percentage of handled contacts where policy displayed on the CAP website matches the Standardized Issue Code (SIC) used by the associate.',
      },
      formula: {
        id: 'cs_perfportal_pa_formula',
        defaultMessage:
          'Contacts where policy displayed by CAP website matches SIC used / Total handled contacts',
      },
    },
  },
  PRRNC: {
    titleMessage: {
      id: 'cs_perfportal_prr',
      defaultMessage: 'PRR',
    },
    subTitleMessage: {
      id: 'cs_perfportal_noConcession',
      defaultMessage: 'No Concession',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_prrnc_definition',
        defaultMessage: `is the percentage of "Yes" responses out of the total contacts with responses to the How's My Driving (HMD) survey and that had no concession issued.`,
      },
      formula: {
        id: 'cs_perfportal_prrnc_formula',
        defaultMessage:
          'HMD Yes with NO concession issued / Total HMD Responses of contacts with NO concession issued',
      },
    },
  },
  PRRCI: {
    titleMessage: {
      id: 'cs_perfportal_prr',
      defaultMessage: 'PRR',
    },
    subTitleMessage: {
      id: 'cs_perfportal_concessionIssued',
      defaultMessage: 'Concession Issued',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_prrci_definition',
        defaultMessage: `is the percentage of "Yes" responses out of the total contacts with responses to the How's My Driving (HMD) survey and that had a concession issued.`,
      },
      formula: {
        id: 'cs_perfportal_prrci_formula',
        defaultMessage:
          'HMD Yes with a concession issued / Total HMD Responses of contacts with a concession issued',
      },
    },
  },
  CONCESSION_RATE: {
    titleMessage: {
      id: 'cs_perfportal_concessionRate',
      defaultMessage: 'Concession Rate',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_concessionRate_definition',
        defaultMessage: `is the percentage of resolved contacts for which the associate issued a concession.`,
      },
      formula: {
        id: 'cs_perfportal_concessionRate_formula',
        defaultMessage:
          'Resolved Contacts with Concessions / Resolved Contacts',
      },
    },
  },
  UNDERSTANDABILITY: {
    titleMessage: {
      id: 'cs_perfportal_lighthouse_understandability',
      defaultMessage: 'Understandability',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_lighthouse_understandability_definition',
        defaultMessage:
          'is the percentage of 4 to 5-star responses to the “Easy to Understand” CCX rating',
      },
      formula: {
        id: 'cs_perfportal_understandability_formula',
        defaultMessage:
          'Positive Understandability Responses / Total Understandability Responses',
      },
    },
  },
  UNDERSTANDABILITY_NO_CONCESSION: {
    titleMessage: {
      id: 'cs_perfportal_lighthouse_understandability_noConcession',
      defaultMessage: 'Understandability - No Concession',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_lighthouse_understandability_noConcession_def',
        defaultMessage:
          'Percentage of 4 to 5-star responses to the “Easy to Understand” CCX rating of contacts where a concession was NOT issued.',
      },
      formula: {
        id: 'cs_perfportal_understandability_noConcession_formula',
        defaultMessage:
          'Positive Understandability (No Concession) Responses / Total Understandability (No Concession) Responses',
      },
    },
  },
  UNDERSTANDABILITY_CONCESSION_ISSUED: {
    titleMessage: {
      id: 'cs_perfportal_lighthouse_understandability_concessionIssued',
      defaultMessage: 'Understandability - Concession Issued',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_lighthouse_understandability_concessionIssued_def',
        defaultMessage:
          'Percentage of 4 to 5-star responses to the “Easy to Understand” CCX rating of contacts where a concession was issued.',
      },
      formula: {
        id: 'cs_perfportal_understandability_concessionIssued_formula',
        defaultMessage:
          'Positive Understandability (Concession Issued) Responses / Total Understandability (Concession Issued) Responses',
      },
    },
  },
  PRR: {
    titleMessage: {
      id: 'cs_perfportal_prr',
      defaultMessage: 'PRR',
    },
  },
  HMD_OFFER_RATE: {
    titleMessage: {
      id: 'cs_perfporta_lighthouse_hmdOfferRate',
      defaultMessage: 'HMD Offer Rate',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfporta_lighthouse_hmdOfferRate_def',
        defaultMessage:
          'Percentage of contacts for which a HMD poll was sent out the total contacts resolved by the associate.',
      },
      formula: {
        id: 'cs_perfporta_lighthouse_hmdOfferRate_formula',
        defaultMessage:
          'HMD polls sent out / Total contacts resolved by the associate',
      },
    },
  },
  HMD_RESPONSE_RATE: {
    titleMessage: {
      id: 'cs_perfportal_responseRate',
      defaultMessage: 'HMD Offer & Answer Rate',
    },
  },
  QCS: {
    titleMessage: {
      id: 'cs_perfportal_qcs',
      defaultMessage: 'QCS',
    },
    subTitleMessage: {
      id: 'cs_perfportal_qualityContactScore',
      defaultMessage: 'Quality Contact Score',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_qcs_definition',
        defaultMessage:
          'is the performance indicator on a scale of 50 to 100, combining multiple performance metric scores.',
      },
      formula: {
        id: 'cs_perfportal_qcs_formula_treatment_one',
        defaultMessage:
          'QCS = (Overall Service Score × 40%) + (ACHT Score × 20%) + (Transfer Rate Score × 30%) + (DART Usage Score x 10%)',
      },
    },
  },
  QCS_RAP: {
    titleMessage: {
      id: 'cs_perfportal_rap',
      defaultMessage: 'RAP',
    },
    subTitleMessage: {
      id: 'cs_perfportal_rap_subtitle',
      defaultMessage: 'Repeat Adjusted Positive Response Rate',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_rap_definition',
        defaultMessage:
          'is the percentage of "Yes" responses, after adjustments for repeat contacts in the following 48 hours, out of the total HMD responses.',
      },
      formula: {
        id: 'cs_perfportal_rap_formula',
        defaultMessage:
          'HMD Yes Adjusted / (HMD Yes Adjusted + HMD No Adjusted)',
      },
    },
    submetric: {
      totalHMDResponses: {
        id: 'cs_perfportal_totalHMDResponses',
        defaultMessage: 'Total HMD Responses',
      },
      hmdYesAdjusted: {
        id: 'cs_perfportal_hmdYesAdjusted',
        defaultMessage: 'HMD Yes Adjusted',
      },
      hmdYesResponses: {
        id: 'cs_perfportal_hmdYesResponses',
        defaultMessage: 'HMD Yes Responses',
      },
    },
  },
  FCR: {
    titleMessage: {
      id: 'cs_perfportal_fcr',
      defaultMessage: 'FCR',
    },
    subTitleMessage: {
      id: 'cs_perfportal_fcr_subtitle',
      defaultMessage: 'First Contact Resolution',
    },
    popoverMessage: {
      definition: {
        id: 'cs_perfportal_fcr_definition',
        defaultMessage:
          'is the percentage of resolved contacts that did not result in a repeat contact from the customer in the following 7 days.',
      },
      formula: {
        id: 'cs_perfportal_fcr_formula',
        defaultMessage:
          '1 - (Resolved Contacts with Medium or High Confidence Repeat / Repeat Eligible Resolved Contacts)',
      },
    },
  },
};

export const EmptyLighthouse: LighthouseMetrics = {
  userID: '',
  metricTypes: [],
  latestMetricPublishDate: EPOCH_START,
};

export const EmptyFilters: LighthouseFilters = {
  segments: ['ALL'],
  skills: ['ALL'],
  channels: ['ALL'],
  marketplaces: ['ALL'],
  verticals: [],
};

export const domainCalculator = (
  trend: TrendDatapoint[] | undefined,
  percentage: boolean,
  time: boolean
): number[] => {
  if (!trend) return [0, 0];
  if (percentage) return [0, 100];

  if (
    trend?.filter(x => !!x.networkAverage).length === 0 &&
    trend?.filter(x => !!x.datapoint).length === 0
  ) {
    return [0, 10];
  }

  let maxNetworkAvg = Math.max.apply(
    Math,
    trend
      ?.filter(x => !!x.networkAverage)
      .map(trend => {
        return Number.parseInt(trend.networkAverage!);
      })
  );

  let maxDatapoint = Math.max.apply(
    Math,
    trend
      ?.filter(x => !!x.datapoint)
      .map(trend => {
        return Number.parseInt(trend.datapoint!);
      })
  );

  maxNetworkAvg = time ? maxNetworkAvg + 1 : maxNetworkAvg;
  maxDatapoint = time ? maxDatapoint + 1 : maxDatapoint;

  let minNetworkAvg = Math.min.apply(
    Math,
    trend
      ?.filter(x => !!x.networkAverage)
      .map(trend => {
        return Number.parseInt(trend.networkAverage!);
      })
  );

  let minDatapoint = Math.min.apply(
    Math,
    trend
      ?.filter(x => !!x.datapoint)
      .map(trend => {
        return Number.parseInt(trend.datapoint!);
      })
  );

  const dataMax = Math.max(maxNetworkAvg, maxDatapoint);

  const dataMin = Math.min(minNetworkAvg, minDatapoint);

  return [dataMin, dataMax];
};

// @TODO: Need to confirm the value - what we want to display when default vertical is null
export const EmptyAggregatedLightMetrics: LighthouseMetrics = {
  userID: '',
  metricTypes: ['ACHT', 'CCX', 'RAP', 'TR'],
  latestMetricPublishDate: EPOCH_START,
  RAP: {
    topLevelMetrics: {
      baseMetric: {
        datapoint: '0',
        networkAverage: '0',
      },
      change: '0',
      previousDate: 'N/A',
    },
  },
  CCX: {
    topLevelMetrics: {
      baseMetric: {
        datapoint: '0',
        networkAverage: '0',
      },
      change: '0',
      previousDate: 'N/A',
    },
  },
  TR: {
    topLevelMetrics: {
      baseMetric: {
        datapoint: '0',
        networkAverage: '0',
      },
      change: '0',
      previousDate: 'N/A',
    },
  },
  ACHT: {
    topLevelMetrics: {
      baseMetric: {
        datapoint: '0',
        networkAverage: '0',
      },
      change: '0',
      previousDate: 'N/A',
    },
  },
};

const cscEntityPrefix = getCscEntityPrefix();

export const parseUserIdWithPrefix = (directReport: ExtendedUser): string => {
  if (!directReport) {
    return '';
  }
  return `${cscEntityPrefix}:${directReport.userID}`;
};

export const buildEmptyAggregatedForDirectReports = (
  profiles: ExtendedUser[]
): LighthouseMetrics[] => {
  let emptyAggregatedLightMetricsForDirectReports: LighthouseMetrics[] = [];

  profiles.forEach(profile => {
    emptyAggregatedLightMetricsForDirectReports.push({
      ...EmptyAggregatedLightMetrics,
      userID: parseUserIdWithPrefix(profile),
    });
  });

  return emptyAggregatedLightMetricsForDirectReports;
};

export const isTmCheck = (profile: TeamProfile): boolean => {
  if (
    profile.reportingTeam &&
    profile.reportingTeam.filter(
      user => user.relatedUser.relationToUser === 'DIRECT_REPORT'
    ).length > 0
  )
    return true;
  return false;
};

export const NoDataPrinter = (isBold: boolean) => {
  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);
  return isBold ? (
    <span style={{ paddingTop: legacyExperience ? '0px' : '10px' }}>
      <Text type={SUBMETRIC_FONT_SIZE}>
        <b>
          <FormattedMessage
            id='cs_perfportal_no_data'
            defaultMessage='No data'
          />
        </b>
      </Text>
    </span>
  ) : (
    <span style={{ paddingTop: legacyExperience ? '0px' : '10px' }}>
      <Text type={SUBMETRIC_FONT_SIZE}>
        <FormattedMessage id='cs_perfportal_no_data' defaultMessage='No data' />
      </Text>
    </span>
  );
};

const emptyMetricCheck = (metric?: BaseMetric) => {
  return (
    metric == null ||
    (metric?.datapoint === null &&
      (metric.networkAverage === null || metric.networkAverage === '--'))
  );
};

export const CCXDataCheck = (CCXSubmetrics: CCXSubMetrics): boolean => {
  const {
    understandability,
    attentive,
    friendliness,
    knowledgeable,
    empathy,
    courtesy,
    helpfulness,
    efficiency,
  } = CCXSubmetrics;

  const understandabilityCheck = emptyMetricCheck(understandability);
  const attentiveCheck = emptyMetricCheck(attentive);
  const friendlinessCheck = emptyMetricCheck(friendliness);
  const knowledgeableCheck = emptyMetricCheck(knowledgeable);
  const empathyCheck = emptyMetricCheck(empathy);
  const courtesyCheck = emptyMetricCheck(courtesy);
  const helpfulnessCheck = emptyMetricCheck(helpfulness);
  const efficiencyCheck = emptyMetricCheck(efficiency);

  return (
    attentiveCheck &&
    friendlinessCheck &&
    knowledgeableCheck &&
    understandabilityCheck &&
    empathyCheck &&
    courtesyCheck &&
    helpfulnessCheck &&
    efficiencyCheck
  );
};

export const CCXNegDataCheck = (
  CCXNegSubMetrics: CCXNegSubMetrics
): boolean => {
  const {
    negativeUnderstandability,
    negativeKnowledgeable,
    negativeEmpathy,
    negativeCourtesy,
    negativeHelpfulness,
    negativeEfficiency,
  } = CCXNegSubMetrics;

  const negativeUnderstandabilityCheck = emptyMetricCheck(
    negativeUnderstandability
  );
  const negativeKnowledgeableCheck = emptyMetricCheck(negativeKnowledgeable);
  const negativeEmpathyCheck = emptyMetricCheck(negativeEmpathy);
  const negativeCourtesyCheck = emptyMetricCheck(negativeCourtesy);
  const negativeHelpfulnessCheck = emptyMetricCheck(negativeHelpfulness);
  const negativeEfficiencyCheck = emptyMetricCheck(negativeEfficiency);

  return (
    negativeKnowledgeableCheck &&
    negativeUnderstandabilityCheck &&
    negativeEmpathyCheck &&
    negativeCourtesyCheck &&
    negativeHelpfulnessCheck &&
    negativeEfficiencyCheck
  );
};

export const CCXNegOneboxDataCheck = (
  CCXNegOneboxSubMetrics: CCXNegOneboxSubMetrics
): boolean => {
  const {
    negativeOneboxUnderstandability,
    negativeOneboxKnowledgeable,
    negativeOneboxEmpathy,
    negativeOneboxCourtesy,
    negativeOneboxHelpfulness,
    negativeOneboxEfficiency,
  } = CCXNegOneboxSubMetrics;

  const negativeOneboxUnderstandabilityCheck = emptyMetricCheck(
    negativeOneboxUnderstandability
  );
  const negativeOneboxKnowledgeableCheck = emptyMetricCheck(
    negativeOneboxKnowledgeable
  );
  const negativeOneboxEmpathyCheck = emptyMetricCheck(negativeOneboxEmpathy);
  const negativeOneboxCourtesyCheck = emptyMetricCheck(negativeOneboxCourtesy);
  const negativeOneboxHelpfulnessCheck = emptyMetricCheck(
    negativeOneboxHelpfulness
  );
  const negativeOneboxEfficiencyCheck = emptyMetricCheck(
    negativeOneboxEfficiency
  );

  return (
    negativeOneboxKnowledgeableCheck &&
    negativeOneboxUnderstandabilityCheck &&
    negativeOneboxEmpathyCheck &&
    negativeOneboxCourtesyCheck &&
    negativeOneboxHelpfulnessCheck &&
    negativeOneboxEfficiencyCheck
  );
};

export const ACHTDataCheck = (ACHTSubmetrics: ACHTSubMetrics): boolean => {
  const {
    acwTime,
    acwTimeInbound,
    acwTimeOutbound,
    holdTime,
    ttcForMessageUs,
    highSilenceRate,
  } = ACHTSubmetrics;

  const acwTimeCheck = emptyMetricCheck(acwTime);
  const acwTimeInboundCheck = emptyMetricCheck(acwTimeInbound);
  const acwTimeOutboundCheck = emptyMetricCheck(acwTimeOutbound);
  const holdTimeCheck = emptyMetricCheck(holdTime);
  const ttcForMessageUsCheck = emptyMetricCheck(ttcForMessageUs);
  const highSilenceRateCheck = emptyMetricCheck(highSilenceRate);

  return (
    acwTimeCheck &&
    acwTimeInboundCheck &&
    acwTimeOutboundCheck &&
    holdTimeCheck &&
    ttcForMessageUsCheck &&
    highSilenceRateCheck
  );
};

export const ConcessionRateDataCheck = (
  concessionRateSubMetrics: ConcessionRateSubMetrics
): boolean => {
  const { promoteGCIssuedCount, oopExceptions, concessionIssuedPerContact } =
    concessionRateSubMetrics;

  const promoteGCIssuedCountCheck = emptyMetricCheck(promoteGCIssuedCount);
  const oopExceptionsCheck = emptyMetricCheck(oopExceptions);
  const concessionIssuedPerContactCheck = emptyMetricCheck(
    concessionIssuedPerContact
  );

  return (
    promoteGCIssuedCountCheck &&
    oopExceptionsCheck &&
    concessionIssuedPerContactCheck
  );
};

export const ConsultRateDataCheck = (
  consultRateSubMetrics: ConsultRateSubMetrics
): boolean => {
  const { consults, phoneHandledContacts } = consultRateSubMetrics;

  const consultsCheck = emptyMetricCheck(consults);
  const phoneHandledContactsCheck = emptyMetricCheck(phoneHandledContacts);

  return consultsCheck && phoneHandledContactsCheck;
};

export const DARTRateDataCheck = (
  DARTRateSubMetrics: DARTSubMetrics
): boolean => {
  const { dartUsedCount, totalHandledContact, escalatedContacts } =
    DARTRateSubMetrics;

  const dartUsedCountCheck = emptyMetricCheck(dartUsedCount);
  const totalHandledContactCheck = emptyMetricCheck(totalHandledContact);
  const escalatedContactsCheck = emptyMetricCheck(escalatedContacts);

  return (
    dartUsedCountCheck && totalHandledContactCheck && escalatedContactsCheck
  );
};

export const DCRDataCheck = (dcrSubMetrics: DCRSubMetrics): boolean => {
  const { giftCardsIssuedCount, oowExceptions } = dcrSubMetrics;

  const giftCardsIssuedCountCheck = emptyMetricCheck(giftCardsIssuedCount);
  const oowExceptionsCheck = emptyMetricCheck(oowExceptions);

  return giftCardsIssuedCountCheck && oowExceptionsCheck;
};

export const DSRDataCheck = (DCRSubMetrics: DSRSubMetrics): boolean => {
  const { dsrDenominator, dsrYes } = DCRSubMetrics;

  const dsrDenominatorCheck = emptyMetricCheck(dsrDenominator);
  const dsrYesCheck = emptyMetricCheck(dsrYes);

  return dsrDenominatorCheck && dsrYesCheck;
};

export const SDRDataCheck = (SDRSubMetricsData: SDRSubMetrics): boolean => {
  const { sdrDenominator, sdrYes } = SDRSubMetricsData;

  const sdrDenominatorCheck = emptyMetricCheck(sdrDenominator);
  const sdrYesCheck = emptyMetricCheck(sdrYes);

  return sdrDenominatorCheck && sdrYesCheck;
};

export const PolicyAdherenceDataCheck = (
  policyAdherenceSubmetrics: PolicyAdherenceSubmetrics
): boolean => {
  const { concessionGivenRate } = policyAdherenceSubmetrics;

  const concessionGivenRateCheck = emptyMetricCheck(concessionGivenRate);

  return concessionGivenRateCheck;
};

export const PRRCIDataCheck = (
  prrConcessionIssuedDataSubmetrics: PRRConcessionIssuedDataSubmetrics
): boolean => {
  const { understandabilityConcessionIssued } =
    prrConcessionIssuedDataSubmetrics;

  const understandabilityConcessionIssuedCheck = emptyMetricCheck(
    understandabilityConcessionIssued
  );

  return understandabilityConcessionIssuedCheck;
};

export const PRRNCDataCheck = (
  prrNoConcessionDataSubmetrics: PRRNoConcessionDataSubmetrics
): boolean => {
  const { understandabilityNoConcession } = prrNoConcessionDataSubmetrics;

  const understandabilityNoConcessionCheck = emptyMetricCheck(
    understandabilityNoConcession
  );

  return understandabilityNoConcessionCheck;
};

export const RAPDataCheck = (rapSubmetrics: RAPSubmetrics): boolean => {
  const {
    totalHmdResponses,
    hmdYesAdjustedCount,
    hmdYesResponses,
    hmdNoResponses,
    rapRepeatCount,
    prr,
    fcr,
    nonOrderTaggingRate,
    hmdOfferRate,
    hmdResponseRate,
  } = rapSubmetrics;

  const totalHmdResponsesCheck = emptyMetricCheck(totalHmdResponses);
  const hmdYesAdjustedCountCheck = emptyMetricCheck(hmdYesAdjustedCount);
  const hmdYesResponsesCheck = emptyMetricCheck(hmdYesResponses);
  const hmdNoResponsesCheck = emptyMetricCheck(hmdNoResponses);
  const rapRepeatCountCheck = emptyMetricCheck(rapRepeatCount);
  const prrCheck = emptyMetricCheck(prr);
  const fcrCheck = emptyMetricCheck(fcr);
  const nonOrderTaggingRateCheck = emptyMetricCheck(nonOrderTaggingRate);
  const hmdOfferRateCheck = emptyMetricCheck(hmdOfferRate);
  const hmdResponseRateCheck = emptyMetricCheck(hmdResponseRate);

  return (
    totalHmdResponsesCheck &&
    hmdYesAdjustedCountCheck &&
    hmdYesResponsesCheck &&
    hmdNoResponsesCheck &&
    rapRepeatCountCheck &&
    prrCheck &&
    fcrCheck &&
    nonOrderTaggingRateCheck &&
    hmdOfferRateCheck &&
    hmdResponseRateCheck
  );
};

export const TRDataCheck = (
  transferRateSubMetrics: TransferRateSubMetrics
): boolean => {
  const {
    transferredContacts,
    muParkedRate,
    handledContacts,
    boomerangContactCount,
    consults,
    resolvedContacts,
    escalatedContacts,
    resolveRate,
    lockToResolve,
  } = transferRateSubMetrics;

  const transferredContactsCheck = emptyMetricCheck(transferredContacts);
  const muParkedRateCheck = emptyMetricCheck(muParkedRate);
  const handledContactsCheck = emptyMetricCheck(handledContacts);
  const boomerangContactCountCheck = emptyMetricCheck(boomerangContactCount);
  const consultsCheck = emptyMetricCheck(consults);
  const resolvedContactsCheck = emptyMetricCheck(resolvedContacts);
  const escalatedContactsCheck = emptyMetricCheck(escalatedContacts);
  const resolveRateCheck = emptyMetricCheck(resolveRate);
  const lockToResolveCheck = emptyMetricCheck(lockToResolve);

  return (
    transferredContactsCheck &&
    muParkedRateCheck &&
    handledContactsCheck &&
    boomerangContactCountCheck &&
    consultsCheck &&
    resolvedContactsCheck &&
    escalatedContactsCheck &&
    resolveRateCheck &&
    lockToResolveCheck
  );
};

export const UnderstandabilityDataCheck = (
  understanabilitySubMetrics: UnderstanabilitySubMetrics
): boolean => {
  const { attentive, friendliness, knowledgeable, ccxOverallService } =
    understanabilitySubMetrics;

  const ccxOverallServiceCheck = emptyMetricCheck(ccxOverallService);
  const attentiveCheck = emptyMetricCheck(attentive);
  const friendlinessCheck = emptyMetricCheck(friendliness);
  const knowledgeableCheck = emptyMetricCheck(knowledgeable);

  return (
    ccxOverallServiceCheck &&
    attentiveCheck &&
    friendlinessCheck &&
    knowledgeableCheck
  );
};

export const HMDOfferRateDataCheck = (
  hmdOfferRateSubMetrics: HMDOfferRateSubMetrics
): boolean => {
  const { totalHandledContact } = hmdOfferRateSubMetrics;

  const hmdOfferRateCheck = emptyMetricCheck(totalHandledContact);

  return hmdOfferRateCheck;
};

export const PRRDataCheck = (prrSubMetrics: PRRSubMetrics): boolean => {
  const { hmdYesResponses, hmdNoResponses } = prrSubMetrics;

  const hmdYesResponsesCheck = hmdYesResponses?.datapoint === null;
  const hmdNoResponsesCheck = hmdNoResponses?.datapoint === null;

  return hmdYesResponsesCheck && hmdNoResponsesCheck;
};

export const AnswerRateDataCheck = (
  hmdResponseRateSubMetrics: HMDResponseRateSubMetrics
): boolean => {
  const { totalHmdResponses, offerCount } = hmdResponseRateSubMetrics;
  const totalHmdResponsesCheck = totalHmdResponses?.datapoint === null;
  const offerCountCheck = offerCount?.datapoint === null;

  return totalHmdResponsesCheck && offerCountCheck;
};

export const isQCSRelatedVertical = (vertical: string) => {
  if (QCSVertical.includes(vertical)) return true;
  else return false;
};

const isValidTimestamp = (timestamp: string) => {
  const dateRegex = /[0-9]{2}\-[0-9]{2}$/;
  return dateRegex.test(timestamp);
};

export const isValidDateRange = (startDate: string, endDate: string) => {
  if (isValidTimestamp(startDate) && isValidTimestamp(endDate)) {
    return validateDateRange(startDate, endDate);
  }
  return false;
};

export const getDefaultDateRange = () => {
  // set default to be the recent 7 days
  const today = new Date();
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  const initDate = new Date();
  initDate.setTime(today.getTime() - sevenDays);
  const defaultDateRange = [
    moment(initDate).format('YYYY-MM-DD'),
    moment(today).format('YYYY-MM-DD'),
  ];
  return defaultDateRange;
};

export const getTeam = (profile: TeamProfile) => {
  return isTmCheck(profile)
    ? profile.reportingTeam.map(member => member.relatedUser)
    : [];
};

export const getTeamIsTM = (profile: TeamProfile) => {
  return isTmCheck(profile)
    ? profile.reportingTeam.map(member => member.isManager)
    : [];
};

export const refreshFilters = (
  payload: LighthouseMetrics[],
  setLatestMetricPublishDate: any,
  setSyncTimeChangeCount: any
) => {
  // refresh filters if sync time has changed
  if (payload.length <= 0) return;
  setLatestMetricPublishDate((prevLatestMetricPublishDate: string): string => {
    if (
      prevLatestMetricPublishDate !== EPOCH_START &&
      payload[0].latestMetricPublishDate !== EPOCH_START &&
      prevLatestMetricPublishDate !== payload[0].latestMetricPublishDate
    ) {
      setSyncTimeChangeCount((count: number) => count + 1);
    }

    return payload[0].latestMetricPublishDate !== EPOCH_START
      ? payload[0].latestMetricPublishDate
      : prevLatestMetricPublishDate;
  });
};

export const getCardTypeMessageMap = (type: string): any => {
  const enableRAPRemovalAndCCXUpdate = isFeatureEnabled(
    Feature.ENABLE_RAP_REMOVAL_AND_CCX_UPDATE
  );
  const cardType =
    enableRAPRemovalAndCCXUpdate && type === 'CCX' ? 'CCX_V2' : type;
  return CARD_TYPE_MESSAGE_MAP[cardType as keyof TypeMessageMap];
};
